/**
 * MULTI-DOMAIN SITE CONFIGURATION
 * Dr. Deepthi Nandan Reddy - Upper Limb Specialist
 * 
 * This configuration powers 4 specialized websites:
 * 1. shoulder.co.in - Orange theme (Saffron)
 * 2. elbow.co.in - White/Grey/Black theme
 * 3. handandwrist.co.in - Green theme
 * 4. drdnreddyortho.com - Main hub with unique theme
 */

// Site identifiers
export const SITES = {
  SHOULDER: 'shoulder',
  ELBOW: 'elbow',
  HAND_WRIST: 'handandwrist',
  MAIN: 'main'
};

// Color themes based on Indian tricolor concept
export const THEMES = {
  shoulder: {
    primary: '24 80% 45%',      // Burnt Orange - Professional
    primaryHex: '#cc5500',
    accent: '24 80% 45%',
    accentHex: '#cc5500',
    gradient: 'from-orange-700 to-orange-800',
    bgGradient: 'from-orange-50 to-white',
    lightBg: 'bg-orange-50',
    lightBgHex: '#fff7ed',
    textClass: 'text-orange-700',
    bgClass: 'bg-orange-700',
    borderClass: 'border-orange-700',
    hoverBg: 'hover:bg-orange-800',
    name: 'Burnt Orange'
  },
  elbow: {
    primary: '220 13% 18%',     // Elegant Dark Slate
    primaryHex: '#27272a',
    accent: '220 9% 46%',
    accentHex: '#71717a',
    gradient: 'from-zinc-700 to-zinc-900',
    bgGradient: 'from-zinc-50 to-white',
    lightBg: 'bg-zinc-100',
    lightBgHex: '#f4f4f5',
    textClass: 'text-zinc-700',
    bgClass: 'bg-zinc-800',
    borderClass: 'border-zinc-700',
    hoverBg: 'hover:bg-zinc-900',
    name: 'Elegant Slate'
  },
  handandwrist: {
    primary: '142 71% 45%',     // Indian Green
    primaryHex: '#22c55e',
    accent: '142 71% 45%',
    accentHex: '#22c55e',
    gradient: 'from-green-500 to-green-600',
    bgGradient: 'from-green-50 to-white',
    lightBg: 'bg-green-50',
    lightBgHex: '#f0fdf4',
    textClass: 'text-green-500',
    bgClass: 'bg-green-500',
    borderClass: 'border-green-500',
    hoverBg: 'hover:bg-green-600',
    name: 'Indian Green'
  },
  main: {
    primary: '220 90% 56%',     // Royal Blue
    primaryHex: '#3b82f6',
    accent: '24 95% 53%',
    accentHex: '#f97316',
    gradient: 'from-blue-600 to-indigo-700',
    bgGradient: 'from-blue-50 to-white',
    lightBg: 'bg-blue-50',
    lightBgHex: '#eff6ff',
    textClass: 'text-blue-600',
    bgClass: 'bg-blue-600',
    borderClass: 'border-blue-600',
    hoverBg: 'hover:bg-blue-700',
    name: 'Royal Blue'
  }
};

// Site-specific content configurations
export const SITE_CONFIGS = {
  shoulder: {
    id: SITES.SHOULDER,
    domain: 'shoulder.co.in',
    title: 'Shoulder.co.in',
    tagline: 'Your Complete Shoulder Care Resource',
    heroTitle: 'Shoulder Pain?',
    heroSubtitle: 'Get Expert Care',
    heroDescription: 'Comprehensive shoulder care information from an experienced orthopaedic specialist. Understand your condition and explore the best treatment options.',
    specialtyLabel: 'Shoulder Specialist',
    bodyPart: 'Shoulder',
    metaDescription: 'Expert shoulder care by Dr. Deepthi Nandan Reddy. Learn about rotator cuff tears, frozen shoulder, shoulder replacement and more.',
    theme: THEMES.shoulder,
    icon: '💪',
    anatomyImage: 'shoulder',
    // Categorized conditions for better navigation
    conditionCategories: [
      {
        id: 'degenerative',
        name: 'Degenerative & Wear',
        icon: '🔄',
        conditions: [
          { id: 'rotator-cuff-tears', title: 'Rotator Cuff Tears', slug: '/rotator-cuff-tear' },
          { id: 'shoulder-arthritis', title: 'Shoulder Arthritis', slug: '/shoulder-arthritis' },
          { id: 'shoulder-impingement', title: 'Shoulder Impingement', slug: '/shoulder-impingement' },
          { id: 'frozen-shoulder', title: 'Frozen Shoulder', slug: '/frozen-shoulder' },
          { id: 'calcific-tendonitis', title: 'Calcific Tendonitis', slug: '/calcific-tendonitis' },
          { id: 'biceps-tendon', title: 'Biceps Tendon Problems', slug: '/biceps-tendon-injury' },
        ]
      },
      {
        id: 'trauma',
        name: 'Trauma & Fractures',
        icon: '🦴',
        conditions: [
          { id: 'shoulder-fracture', title: 'Proximal Humerus Fractures', slug: '/shoulder-fracture' },
          { id: 'clavicle-fracture', title: 'Clavicle Fractures', slug: '/clavicle-fracture' },
          { id: 'scapula-fracture', title: 'Scapula Fractures', slug: '/scapula-fracture' },
          { id: 'ac-joint', title: 'AC Joint Injuries', slug: '/ac-joint-injury' },
          { id: 'sternoclavicular', title: 'SC Joint Injuries', slug: '/sc-joint-injury' },
        ]
      },
      {
        id: 'instability',
        name: 'Instability & Sports',
        icon: '⚡',
        conditions: [
          { id: 'shoulder-dislocation', title: 'Shoulder Dislocation', slug: '/shoulder-dislocation' },
          { id: 'slap-tears', title: 'SLAP Tears', slug: '/slap-tears' },
          { id: 'labral-tears', title: 'Labral Tears', slug: '/labral-tears' },
          { id: 'multidirectional-instability', title: 'Multidirectional Instability', slug: '/mdi' },
        ]
      },
      {
        id: 'surgery',
        name: 'Surgical Procedures',
        icon: '🏥',
        conditions: [
          { id: 'shoulder-replacement', title: 'Shoulder Replacement', slug: '/shoulder-replacement' },
          { id: 'arthroscopy', title: 'Arthroscopic Surgery', slug: '/arthroscopic-shoulder-surgery' },
          { id: 'reverse-shoulder', title: 'Reverse Shoulder Replacement', slug: '/reverse-shoulder-replacement' },
        ]
      },
      {
        id: 'general',
        name: 'General',
        icon: '📋',
        conditions: [
          { id: 'shoulder-pain', title: 'Shoulder Pain Guide', slug: '/shoulder-pain' },
        ]
      }
    ],
    // Flat list for routing (all conditions)
    conditions: [
      { id: 'rotator-cuff-tears', title: 'Rotator Cuff Tears', slug: '/rotator-cuff-tear', description: 'Complete guide to rotator cuff injuries, symptoms, and surgical repair options' },
      { id: 'frozen-shoulder', title: 'Frozen Shoulder', slug: '/frozen-shoulder', description: 'Understanding adhesive capsulitis and effective treatment approaches' },
      { id: 'shoulder-dislocation', title: 'Shoulder Instability & Dislocation', slug: '/shoulder-dislocation', description: 'Expert care for recurrent dislocations and shoulder instability' },
      { id: 'shoulder-arthritis', title: 'Shoulder Arthritis', slug: '/shoulder-arthritis', description: 'Managing glenohumeral arthritis and preserving shoulder function' },
      { id: 'shoulder-replacement', title: 'Shoulder Replacement', slug: '/shoulder-replacement', description: 'Total and reverse shoulder arthroplasty for severe arthritis' },
      { id: 'slap-tears', title: 'SLAP Tears', slug: '/slap-tears', description: 'Superior labral tears and treatment in athletes' },
      { id: 'shoulder-impingement', title: 'Shoulder Impingement', slug: '/shoulder-impingement', description: 'Subacromial impingement syndrome diagnosis and treatment' },
      { id: 'biceps-tendon', title: 'Biceps Tendon Injuries', slug: '/biceps-tendon-injury', description: 'Biceps tendonitis, tears, and surgical options' },
      { id: 'ac-joint', title: 'AC Joint Injuries', slug: '/ac-joint-injury', description: 'Acromioclavicular joint separations and arthritis' },
      { id: 'calcific-tendonitis', title: 'Calcific Tendonitis', slug: '/calcific-tendonitis', description: 'Calcium deposits in the rotator cuff and treatment' },
      { id: 'shoulder-fracture', title: 'Shoulder Fractures', slug: '/shoulder-fracture', description: 'Proximal humerus fracture management' },
      { id: 'clavicle-fracture', title: 'Clavicle Fractures', slug: '/clavicle-fracture', description: 'Collarbone fracture treatment and recovery' },
      { id: 'scapula-fracture', title: 'Scapula Fractures', slug: '/scapula-fracture', description: 'Shoulder blade fracture management' },
      { id: 'labral-tears', title: 'Labral Tears', slug: '/labral-tears', description: 'Shoulder labrum injuries and repair' },
      { id: 'mdi', title: 'Multidirectional Instability', slug: '/mdi', description: 'MDI treatment and rehabilitation' },
      { id: 'sternoclavicular', title: 'SC Joint Injuries', slug: '/sc-joint-injury', description: 'Sternoclavicular joint problems' },
      { id: 'reverse-shoulder', title: 'Reverse Shoulder Replacement', slug: '/reverse-shoulder-replacement', description: 'Reverse total shoulder arthroplasty' },
      { id: 'arthroscopy', title: 'Shoulder Arthroscopy', slug: '/arthroscopic-shoulder-surgery', description: 'Minimally invasive keyhole surgery for shoulder conditions' },
      { id: 'shoulder-pain', title: 'Shoulder Pain', slug: '/shoulder-pain', description: 'Understanding and treating shoulder pain' }
    ],
    symptoms: [
      'Persistent pain lasting more than 2 weeks',
      'Difficulty raising your arm overhead',
      'Pain that worsens at night',
      'Shoulder instability or clicking',
      'Weakness when lifting objects',
      'Pain following an injury or fall',
      'Stiffness limiting daily activities',
      'Grinding sensation in the shoulder'
    ],
    relatedSites: ['elbow', 'handandwrist', 'main']
  },

  elbow: {
    id: SITES.ELBOW,
    domain: 'elbow.co.in',
    title: 'Elbow.co.in',
    tagline: 'Expert Elbow Care & Treatment',
    heroTitle: 'Elbow Pain?',
    heroSubtitle: 'Expert Solutions',
    heroDescription: 'Specialized elbow care from diagnosis to recovery. Get comprehensive information on tennis elbow, golfer\'s elbow, and complex elbow conditions.',
    specialtyLabel: 'Elbow Specialist',
    bodyPart: 'Elbow',
    metaDescription: 'Expert elbow treatment by Dr. Deepthi Nandan Reddy. Tennis elbow, golfer\'s elbow, elbow arthroscopy and joint replacement.',
    theme: THEMES.elbow,
    icon: '🦴',
    anatomyImage: 'elbow',
    conditions: [
      { id: 'tennis-elbow', title: 'Tennis Elbow', slug: '/tennis-elbow', description: 'Lateral epicondylitis - causes, treatment and prevention' },
      { id: 'golfers-elbow', title: 'Golfer\'s Elbow', slug: '/golfers-elbow', description: 'Medial epicondylitis treatment and rehabilitation' },
      { id: 'cubital-tunnel', title: 'Cubital Tunnel Syndrome', slug: '/cubital-tunnel', description: 'Ulnar nerve compression at the elbow' },
      { id: 'elbow-arthritis', title: 'Elbow Arthritis', slug: '/elbow-arthritis', description: 'Osteoarthritis and inflammatory arthritis of the elbow' },
      { id: 'elbow-stiffness', title: 'Elbow Stiffness', slug: '/elbow-stiffness', description: 'Causes and treatment of elbow contracture' },
      { id: 'elbow-fracture', title: 'Elbow Fractures', slug: '/elbow-fracture', description: 'Radial head, olecranon, and distal humerus fractures' },
      { id: 'elbow-dislocation', title: 'Elbow Dislocation', slug: '/elbow-dislocation', description: 'Simple and complex elbow dislocations' },
      { id: 'biceps-avulsion', title: 'Distal Biceps Rupture', slug: '/distal-biceps-rupture', description: 'Biceps tendon tears at the elbow' },
      { id: 'triceps-rupture', title: 'Triceps Rupture', slug: '/triceps-rupture', description: 'Triceps tendon injuries and repair' },
      { id: 'ocd-elbow', title: 'Elbow OCD', slug: '/elbow-ocd', description: 'Osteochondritis dissecans in young athletes' },
      { id: 'ucl-injury', title: 'UCL Injury (Tommy John)', slug: '/ucl-injury', description: 'Ulnar collateral ligament injuries in throwers' },
      { id: 'elbow-arthroscopy', title: 'Elbow Arthroscopy', slug: '/elbow-arthroscopy', description: 'Minimally invasive elbow surgery' },
      { id: 'elbow-replacement', title: 'Elbow Replacement', slug: '/elbow-replacement', description: 'Total elbow arthroplasty for severe arthritis' }
    ],
    symptoms: [
      'Pain on the outer side of elbow',
      'Pain on the inner side of elbow',
      'Numbness or tingling in fingers',
      'Difficulty straightening the elbow',
      'Weakness gripping objects',
      'Clicking or locking of elbow',
      'Swelling around the elbow',
      'Pain when lifting or carrying'
    ],
    relatedSites: ['shoulder', 'handandwrist', 'main']
  },

  handandwrist: {
    id: SITES.HAND_WRIST,
    domain: 'handandwrist.co.in',
    title: 'HandAndWrist.co.in',
    tagline: 'Complete Hand & Wrist Care',
    heroTitle: 'Hand or Wrist Pain?',
    heroSubtitle: 'Precision Care',
    heroDescription: 'Expert hand and wrist care for carpal tunnel, trigger finger, fractures, and complex reconstructive surgery. Restore function and relieve pain.',
    specialtyLabel: 'Hand & Wrist Specialist',
    bodyPart: 'Hand & Wrist',
    metaDescription: 'Expert hand and wrist treatment by Dr. Deepthi Nandan Reddy. Carpal tunnel, trigger finger, wrist fractures and microsurgery.',
    theme: THEMES.handandwrist,
    icon: '✋',
    anatomyImage: 'hand',
    conditions: [
      { id: 'carpal-tunnel', title: 'Carpal Tunnel Syndrome', slug: '/carpal-tunnel', description: 'Median nerve compression and treatment options' },
      { id: 'trigger-finger', title: 'Trigger Finger', slug: '/trigger-finger', description: 'Stenosing tenosynovitis - catching and locking of fingers' },
      { id: 'de-quervain', title: 'De Quervain\'s Tenosynovitis', slug: '/de-quervain', description: 'Thumb-side wrist pain and treatment' },
      { id: 'dupuytren', title: 'Dupuytren\'s Contracture', slug: '/dupuytren', description: 'Progressive finger contracture and release options' },
      { id: 'ganglion-cyst', title: 'Ganglion Cysts', slug: '/ganglion-cyst', description: 'Wrist and hand cysts - when to treat' },
      { id: 'scaphoid-fracture', title: 'Scaphoid Fracture', slug: '/scaphoid-fracture', description: 'Common wrist fracture and healing challenges' },
      { id: 'wrist-fracture', title: 'Wrist Fractures', slug: '/wrist-fracture', description: 'Distal radius and other wrist fractures' },
      { id: 'tfcc-injury', title: 'TFCC Injury', slug: '/tfcc-injury', description: 'Triangular fibrocartilage complex tears' },
      { id: 'thumb-arthritis', title: 'Thumb Arthritis', slug: '/thumb-arthritis', description: 'Basilar joint arthritis and treatment' },
      { id: 'wrist-arthritis', title: 'Wrist Arthritis', slug: '/wrist-arthritis', description: 'SLAC and SNAC wrist treatment options' },
      { id: 'finger-fractures', title: 'Finger Fractures', slug: '/finger-fractures', description: 'Metacarpal and phalangeal fractures' },
      { id: 'tendon-injuries', title: 'Tendon Injuries', slug: '/tendon-injuries', description: 'Flexor and extensor tendon repairs' },
      { id: 'nerve-injuries', title: 'Nerve Injuries', slug: '/nerve-injuries', description: 'Peripheral nerve repair and reconstruction' },
      { id: 'wrist-arthroscopy', title: 'Wrist Arthroscopy', slug: '/wrist-arthroscopy', description: 'Minimally invasive wrist surgery' }
    ],
    symptoms: [
      'Numbness or tingling in fingers',
      'Finger catching or locking',
      'Wrist pain with gripping',
      'Difficulty making a fist',
      'Weakness in grip strength',
      'Visible lump on wrist or hand',
      'Swelling after injury',
      'Stiffness in fingers or wrist'
    ],
    relatedSites: ['shoulder', 'elbow', 'main']
  },

  main: {
    id: SITES.MAIN,
    domain: 'drdnreddyortho.com',
    title: 'Dr. Deepthi Nandan Reddy',
    tagline: 'Upper Limb Orthopaedic Specialist',
    heroTitle: 'Upper Limb Pain?',
    heroSubtitle: 'World-Class Care',
    heroDescription: 'Comprehensive upper limb orthopaedic care - shoulder, elbow, hand and wrist. International fellowship trained specialist providing cutting-edge treatment.',
    specialtyLabel: 'Upper Limb Specialist',
    bodyPart: 'Upper Limb',
    metaDescription: 'Dr. Deepthi Nandan Reddy - Upper limb orthopaedic specialist. Expert in shoulder, elbow, hand and wrist surgery. Hyderabad, India.',
    theme: THEMES.main,
    icon: '🏥',
    anatomyImage: 'arm',
    specialties: [
      {
        id: 'shoulder',
        title: 'Shoulder',
        description: 'Rotator cuff repair, shoulder replacement, sports injuries',
        link: 'https://shoulder.co.in',
        icon: '💪',
        theme: THEMES.shoulder
      },
      {
        id: 'elbow',
        title: 'Elbow',
        description: 'Tennis elbow, elbow arthroscopy, joint replacement',
        link: 'https://elbow.co.in',
        icon: '🦴',
        theme: THEMES.elbow
      },
      {
        id: 'handandwrist',
        title: 'Hand & Wrist',
        description: 'Carpal tunnel, trigger finger, microsurgery',
        link: 'https://handandwrist.co.in',
        icon: '✋',
        theme: THEMES.handandwrist
      }
    ],
    conditions: [],
    symptoms: [
      'Pain in shoulder, elbow, hand or wrist',
      'Difficulty with daily activities',
      'Sports-related injuries',
      'Numbness or tingling in arm',
      'Weakness in upper limb',
      'Post-injury pain or stiffness',
      'Arthritis affecting upper limb',
      'Need for second opinion'
    ],
    relatedSites: ['shoulder', 'elbow', 'handandwrist']
  }
};

// Doctor information (shared across all sites)
export const DOCTOR_INFO = {
  name: 'Dr. Deepthi Nandan Reddy',
  nameTeluglу: 'డాక్టర్ డిఎన్ రెడ్డి',
  // Alternate names for SEO entity linking
  alternateNames: ['Dr. A. Deepthi Nandan Reddy', 'Dr. D. N. Reddy', 'Adla Deepthi Nandan Reddy', 'Dr. DN Reddy'],
  credentials: 'MBBS, MS (Orthopaedics), MSc (Ortho, UK), FRCS Ed, FRCS (Ortho, UK), CCT (UK)',
  shortCredentials: 'MS Ortho, FRCS (UK)',
  title: 'Senior Consultant Orthopaedic & Upper-Limb Surgeon',
  shortTitle: 'Shoulder & Elbow Surgeon',
  fellowships: 'Advanced Shoulder, Elbow & Hand Fellowships (UK, Mayo Clinic, Florida Orthopaedic Institute – USA)',
  specialization: 'Shoulder, Elbow, Wrist, Hip, Knee & Sports Injuries',
  medicalSpecialty: 'Orthopedic',
  tagline: 'Combining precision surgery with personalised rehabilitation',
  experience: '30+ Years',
  experienceYears: 30,
  phone: '+91 99599 64567',
  email: 'info@drdnreddyortho.com',
  whatsapp: '919959964567',
  whatsappLink: 'https://api.whatsapp.com/send/?phone=919959964567&text=Hi+Dr.+Deepthi+Nandan+Reddy%2C+I%27d+like+to+schedule+an+appointment.&type=phone_number&app_absent=0',
  hospital: 'Apollo Hospitals',
  hospitalFull: 'Apollo Hospitals, Financial District',
  address: 'D.No-2-105/6/SE, Financial District, Nanakramguda, Serilingampally, Hyderabad, Telangana 500032, India',
  addressShort: 'Financial District, Hyderabad',
  city: 'Hyderabad',
  state: 'Telangana',
  postalCode: '500032',
  country: 'India',
  timings: '9 AM - 5 PM (Mon-Sat)',
  rating: '5.0',
  reviewCount: 250,
  consultationLink: 'https://drdnreddyortho.com/about',
  bookingLink: 'https://www.apollohospitals.com/doctors/orthopedician/hyderabad/dr-deepthi-nandan-reddy',
  googleMapsLink: 'https://maps.app.goo.gl/DYYvquNhSfMuXHRe7',
  doctorImage: 'https://customer-assets.emergentagent.com/job_664f5b05-f9bf-427d-869f-0304c630b7f5/artifacts/d8vss7a1_e146c649-5e7e-46df-a58e-0e1fdc565827.avif',
  patients_treated: '15000+',
  surgeries_performed: '5000+',
  // External profile links for sameAs schema
  externalProfiles: {
    mainWebsite: 'https://drdnreddyortho.com/',
    apolloProfile: 'https://www.apollohospitals.com/doctors/orthopedician/hyderabad/dr-deepthi-nandan-reddy',
    practo: 'https://www.practo.com/hyderabad/doctor/dr-a-deepthi-nandan-reddy-orthopedist',
    justdial: 'https://www.justdial.com/Hyderabad/Dr-Deepthi-Nandan-Reddy-Hi-Tech-City-Gachibowli/040PXX40-XX40-180117094742-R6J3_BZDET',
    instagram: 'https://www.instagram.com/drdnortho/',
    shoulderSite: 'https://shoulder.co.in/',
    elbowSite: 'https://elbow.co.in/',
    handWristSite: 'https://handandwrist.co.in/'
  },
  // Previous positions for SEO entity disambiguation
  previousPositions: [
    'Clinical Director – Upper Limb, Yashoda Hospitals HITEC City',
    'Director of Upper Limb Services, Continental Hospitals',
    'Consultant, University Hospitals of Leicester, UK'
  ],
  positions: [
    {
      title: 'Sr. Consultant Orthopaedic Surgeon',
      organization: 'Apollo Hospitals',
      location: 'Financial District, Hyderabad',
      current: true
    },
    {
      title: 'Director of Upper Limb Services',
      organization: 'Continental Hospital',
      location: 'Hyderabad',
      current: false
    },
    {
      title: 'Consultant Orthopaedic / Shoulder & Hand Surgeon',
      organization: 'University Hospitals of Leicester',
      location: 'United Kingdom',
      current: false
    }
  ],
  memberships: [
    'British Shoulder & Elbow Surgeons',
    'Indian Shoulder & Elbow Society (Founder & EC Member)',
    'Indian Orthopaedic Surgeons Association UK',
    'Indian Orthopaedic Association',
    'Indian Arthroplasty Association',
    'Hand Society of India'
  ],
  socialMedia: {
    youtube: 'https://www.youtube.com/@DrDNReddy',
    linkedin: '',
    instagram: ''
  }
};

// Get site config based on environment or hostname
export const getSiteConfig = () => {
  // Check environment variable first (for build-time configuration)
  const envSite = process.env.REACT_APP_SITE_ID;
  
  if (envSite && SITE_CONFIGS[envSite]) {
    return SITE_CONFIGS[envSite];
  }
  
  // Fallback to hostname detection (for runtime configuration)
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname.toLowerCase();
    
    if (hostname.includes('shoulder')) return SITE_CONFIGS.shoulder;
    if (hostname.includes('elbow')) return SITE_CONFIGS.elbow;
    if (hostname.includes('handandwrist') || hostname.includes('hand')) return SITE_CONFIGS.handandwrist;
    if (hostname.includes('drdnreddy') || hostname.includes('main')) return SITE_CONFIGS.main;
  }
  
  // Default to shoulder for development
  return SITE_CONFIGS.shoulder;
};

// Export default config for direct import
export default SITE_CONFIGS;
